import { MarketRecapWizardState } from "../../types/market-recap";
import { getPromptConfig, getSectorDisplayName } from "./promptConfigs";

/**
 * Formats an array of strings for use in a message.
 * Examples:
 *   ["S&P 500"] => "S&P 500"
 *   ["S&P 500", "Nasdaq"] => "S&P 500 and Nasdaq"
 *   ["S&P 500", "Nasdaq", "Dow Jones"] => "S&P 500, Nasdaq, and Dow Jones"
 */
function formatArrayForMessage(arr: string[]): string {
  if (arr.length === 0) return "";
  if (arr.length === 1) return arr[0];
  if (arr.length === 2) return `${arr[0]} and ${arr[1]}`;
  return `${arr.slice(0, -1).join(", ")}, and ${arr[arr.length - 1]}`;
}

/**
 * Constructs the message to send based on wizard state.
 * Replaces {variable} placeholders with actual values.
 */
export function constructMessage(state: MarketRecapWizardState): string {
  if (!state.promptType) return "";

  const promptConfig = getPromptConfig(state.promptType);
  if (!promptConfig) return "";

  let message = promptConfig.messageTemplate;

  // Replace variable placeholders
  for (const [key, value] of Object.entries(state.variables)) {
    const placeholder = `{${key}}`;

    if (Array.isArray(value)) {
      // Handle arrays (e.g., indices)
      const formatted = formatArrayForMessage(value);
      message = message.replace(placeholder, formatted);
    } else if (key === "sector") {
      // Format sector with display name
      const displayName = getSectorDisplayName(value);
      message = message.replace(placeholder, displayName);
    } else if (key === "ticker") {
      // Ensure ticker is uppercase
      message = message.replace(placeholder, value.toUpperCase());
    } else {
      message = message.replace(placeholder, value);
    }
  }

  return message;
}

/**
 * Validates that all required variables are filled.
 */
export function validateVariables(
  state: MarketRecapWizardState
): { valid: boolean; missing: string[] } {
  if (!state.promptType) {
    return { valid: false, missing: ["promptType"] };
  }

  const promptConfig = getPromptConfig(state.promptType);
  if (!promptConfig) {
    return { valid: false, missing: ["promptType"] };
  }

  const missing: string[] = [];

  for (const variable of promptConfig.variables) {
    if (variable.required) {
      const value = state.variables[variable.name];
      if (!value || (Array.isArray(value) && value.length === 0)) {
        missing.push(variable.label);
      }
    }
  }

  return { valid: missing.length === 0, missing };
}

/**
 * Validates ticker format (1-5 uppercase letters).
 */
export function isValidTicker(ticker: string): boolean {
  return /^[A-Za-z]{1,5}$/.test(ticker);
}
